import React from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { allTenants } from "./../../graphql/queries/tenantQueries";
import {
  deleteTenantsMutation,
  activationPushMutation,
} from "./../../graphql/mutations/tenantMutations";
import Permissions, {
  landlordNoAccessFallbackList,
} from "../../configs/permissions";
import usePermissions from "../../hooks/permissions";
import useBaseNavigation from "./../../hooks/baseNavigation";
import useListSelection from "./../../hooks/rowSelection";
import useNotification from "./../../hooks/notifications";
import useModal from "./../../hooks/modal";
import { TENANT_MODEL_NAME } from "./../../configs/constants";
import { ConfirmActionModal } from "./../../mood-ui/Modals";
import {
  SuccessNotification,
  ErrorNotification,
} from "./../../mood-ui/Notifications";
import PageSpinner from "./../../mood-ui/PageSpinner";
import {
  PageContainer,
  BlockContainer,
  Row,
  Col,
} from "./../../mood-ui/Layout";
import { PageControls, TEditButton, AddButton } from "./../../mood-ui/Controls";
import { ColorLabel, Heading } from "./../../mood-ui/Typography";
import Search from "./../../mood-ui/Search";
import { ListItemCheckbox } from "./../../mood-ui/Controls";
import {
  ItemsNotFound,
  // ColorLabel,
} from "./../../mood-ui/Typography";
import {
  TableContainer,
  TableView,
  THeading,
  TRow,
  TCol,
  TContent,
  THCol,
  // TableActions,
  TableSelectedActions,
  Pagination,
} from "./../../mood-ui/DataTable";

import TenantListActions from "./../../components/list-secondary-actions/TenantListActions";

const ALL_TENANTS = gql(allTenants);
const DELETE_TENANTS = gql(deleteTenantsMutation);

const LandlordTenantList = () => {
  const { id: client, page, limit, orderBy, orderHow, search } = useParams();
  const { protectPage, hasAccess, hasAtLeastOnePermission } = usePermissions();
  protectPage(Permissions.Query.Users, {
    noAccessFallbackList: landlordNoAccessFallbackList,
    variables: { id: client },
  });
  const {
    selectedRows,
    isRowSelected: rowSelected,
    toggleRow: toggleRowSelection,
    clearSelection,
    isAllRowsSelected,
    toggleAllRows,
  } = useListSelection();

  const { modalState, showModal, closeModal } = useModal();
  const {
    notify: displayNotification,
    notificationState,
    handleClose,
  } = useNotification();
  const {
    notify: displayErrorNotification,
    notificationState: errorNotificationState,
    handleClose: handleCloseError,
  } = useNotification();
  const { data, loading, error, refetch } = useQuery(ALL_TENANTS, {
    variables: {
      page: parseInt(page),
      limit: parseInt(limit),
      orderBy,
      orderHow,
      search,
      queryString: `?client=${client}`,
    },
    fetchPolicy: "cache-and-network",
  });
  const { nextPage, prevPage, editForm, createForm, applySearch } =
    useBaseNavigation(TENANT_MODEL_NAME, {
      urlContext: `clients/${client}`,
    });

  const [deleteTenants] = useMutation(DELETE_TENANTS, {
    onCompleted: (result) => {
      displayNotification({
        heading: "Users Deleted",
        message: "Successfully deleted selected users",
        secondsToDisplay: 1,
      });
      clearSelection();
      refetch();
    },
    onError: (e) => {
      displayErrorNotification({
        heading: "Error",
        message: "Failed to delete selected users",
        secondsToDisplay: 2,
      });
    },
  });

  const confirmDeletion = () => {
    showModal({
      heading: "Confirm Deletion",
      message:
        "Are you sure you want to DELETE selected users? Do not proceed if you are not sure.",
      mood: "Danger",
      confirmButtonLabel: "Delete",
      onConfirm: () => {
        closeModal();
        handleDeleteTenants();
      },
      onCancel: () => {
        closeModal();
      },
    });
  };

  const handleDeleteTenants = () => {
    deleteTenants({ variables: { ids: selectedRows } });
  };

  if (error) return <div>Error</div>;

  return (
    <PageContainer>
      <BlockContainer raised>
        <Row tweaks="border-b border-[#dfe2e8] pb-8 mt-0 mx-5">
          <Col width={12}>
            <Heading
              inset={true}
              text="Users"
              description="Users of this client."
            />
          </Col>
        </Row>
        <Row tweaks="pt-5 pb-4 mt-0 px-5">
          <Col width={9}>
            {hasAccess(Permissions.Mutation.CreateUser) && (
              <AddButton
                label="Create User"
                onClick={createForm}
                loading={loading}
              />
            )}
          </Col>
          <Col width={3}>
            <Search onApplySearch={applySearch} />
          </Col>
        </Row>
        <TableContainer tweaks="px-5 pb-5">
          <TableView
            tweaks={
              loading ||
              !data ||
              (data && data.tenants && data.tenants.length === 0)
                ? "border-[#ebedf2]"
                : ""
            }
          >
            <THeading
              tweaks={
                loading ||
                !data ||
                (data && data.tenants && data.tenants.length === 0)
                  ? "opacity-30 border-b"
                  : ""
              }
            >
              <TRow>
                <THCol tweaks="w-4 text-center">
                  <ListItemCheckbox
                    onChange={() =>
                      toggleAllRows(data?.tenants?.map((t) => t.id))
                    }
                    isChecked={
                      isAllRowsSelected(data?.tenants?.map((t) => t.id))
                        ? true
                        : false
                    }
                  />
                </THCol>
                <THCol tweaks="text-left" name="id" value="ID" />
                <THCol tweaks="text-left" name="name" value="Name" />
                <THCol tweaks="text-left" name="email" value="Email" />
                <THCol
                  topSticky="-40px"
                  tweaks="text-left"
                  name="access_type"
                  value="Access"
                />
                <THCol
                  topSticky="-40px"
                  tweaks="text-left"
                  name="status"
                  value="Status"
                />
                <THCol
                  topSticky="-40px"
                  tweaks="text-left"
                  name="account_state"
                  value="Lifecycle"
                />
                <THCol tweaks="w-16 text-center" />
              </TRow>
            </THeading>
            {loading && (
              <tbody>
                <tr>
                  <td colSpan={"100%"}>
                    <PageSpinner inline extraStyles="h-[300px]" />
                  </td>
                </tr>
              </tbody>
            )}
            {!loading && data && data.tenants && data.tenants.length == 0 && (
              <tbody>
                <tr>
                  <td colSpan={"100%"}>
                    <ItemsNotFound text="No users found." tweaks="mx-5" />
                  </td>
                </tr>
              </tbody>
            )}
            {!loading && data && data.tenants && data.tenants.length > 0 && (
              <TContent>
                {data.tenants.map((tenant) => (
                  <TRow key={tenant.id} isSelected={rowSelected(tenant.id)}>
                    <TCol tweaks="w-4 text-center">
                      <ListItemCheckbox
                        onChange={() => toggleRowSelection(tenant.id)}
                        isChecked={rowSelected(tenant.id) ? true : false}
                      />
                    </TCol>
                    <TCol value={tenant.id} />
                    <TCol tweaks="max-w-[180px]">
                      <abbr title={tenant.firstname + " " + tenant.lastname}>
                        {tenant.firstname} {tenant.lastname}
                      </abbr>
                    </TCol>
                    <TCol tweaks="max-w-[180px]">
                      <abbr title={tenant.email}>{tenant.email}</abbr>
                    </TCol>
                    <TCol>
                      {tenant.access_type == "Premium Access" ? (
                        <ColorLabel text="Premium" color="purple" />
                      ) : (
                        <ColorLabel text=" " color="gray" />
                      )}
                    </TCol>
                    <TCol>
                      {tenant.activated ? (
                        <ColorLabel text="Active" color="emerald" />
                      ) : (
                        <ColorLabel text="" color="gray" />
                      )}
                    </TCol>
                    <TCol>
                      {tenant.account_state == "ALIVE" ? (
                        <ColorLabel text="ALIVE" color="emerald" />
                      ) : (
                        <ColorLabel text={tenant.account_state} color="rose" />
                      )}
                    </TCol>

                    <TCol tweaks="w-16 text-center">
                      {hasAtLeastOnePermission([
                        Permissions.Query.User,
                        Permissions.Query.Users,
                      ]) && <TEditButton onClick={() => editForm(tenant.id)} />}
                    </TCol>
                  </TRow>
                ))}
              </TContent>
            )}
          </TableView>
          <PageControls spaced>
            <Row tweaks="w-full pl-5">
              <Col width={4}>
                <TableSelectedActions>
                  {selectedRows && selectedRows.length > 0 && (
                    <TenantListActions
                      onClearSelection={clearSelection}
                      onDelete={confirmDeletion}
                    />
                  )}
                </TableSelectedActions>
              </Col>
              <Col width={5} extraStyles="">
                <div className="flex">
                  {/* dynamic list filter functionality here  */}
                </div>
              </Col>
              <Col width={3}>
                <Pagination onPrev={prevPage} onNext={nextPage} />
              </Col>
            </Row>
          </PageControls>
        </TableContainer>
      </BlockContainer>
      <ConfirmActionModal {...modalState} />
      <SuccessNotification {...notificationState} onClose={handleClose} />
      <ErrorNotification
        {...errorNotificationState}
        onClose={handleCloseError}
      />
    </PageContainer>
  );
};

export default LandlordTenantList;
